import { merge, pick } from 'lodash';
import { useCallback } from 'react';

import { type ShareUrlUpdatedSetting } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';

import { type ShareUrlResponseWithSource } from '~/components/SharePrivateClipModal/types';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type ShareUrlUpdateHandlerProps, useUpdateShortId } from '~/swr-hooks/shortUrl/useUpdateShortId';

/** Fields allowed on `ShortUrl.update` for canvases — identity/list metadata is excluded. */
const UPDATABLE_CANVAS_SHORT_URL_FIELDS = [
  'passcode',
  'disabled',
  'requireAccount',
  'expirationDate',
  'permissions',
  'presentationSettings',
  'presentationContent',
  'presentationPages',
  'boardContext',
] as const;

type UseUpdateShareCanvasLinkParams = {
  canvasId: string;
  shortIdInfo: ShortUrlResponse;
};

export const useUpdateShareCanvasLink = ({ canvasId, shortIdInfo }: UseUpdateShareCanvasLinkParams) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { updateShortId } = useUpdateShortId();

  const onShortUrlUpdate = useCallback(
    async ({
      props,
      analytics,
    }: {
      props: ShareUrlUpdateHandlerProps;
      analytics?: ShareUrlUpdatedSetting;
    }): Promise<ShareUrlResponseWithSource> => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      const response = await updateShortId.mutateAsync({
        objectId: canvasId,
        boardContext: undefined,
        updatePayload: {
          workspaceId,
          ...pick(merge({}, shortIdInfo, props), UPDATABLE_CANVAS_SHORT_URL_FIELDS),
          shortId: shortIdInfo.id,
        },
        analyticsPayload: analytics ? { objectType: 'canvas', setting: analytics } : undefined,
      });

      return {
        ...response,
        objectId: canvasId,
        objectType: 'canvas',
      };
    },
    [canvasId, currentWorkspace?.id, shortIdInfo, updateShortId],
  );

  return {
    onShortUrlUpdate,
    isLoading: updateShortId.isPending,
  };
};
